/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { useRoom } from '../../../hooks/useRoom';
import { notaryAPI } from '../../../api/notary';
import { setCallDetails } from '../../../actions/callActions';
import { useInterval } from '../../../hooks/useInterval';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { useVideoContext } from '../../../contexts/videoProvider/videoContext';
import { isEqual } from '@utils/lodash';
import { parseException } from '@utils/error';

const CALL_DETAILS_REFRESH_INTERVAL = 5000;

export const usePollNotaryCallDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { videoProvider } = useVideoContext();
  const { roomId } = useRoom();
  const callDetails = useRootSelector(state => state.callDetails);
  const [documentsPendingTaggingNames, setDocumentsPendingTaggingNames] = useState<string[]>([]);

  const fetchCallDetails = async () => {
    try {
      const call = await notaryAPI.rooms.fetchCallDetailsForRoom(roomId);

      // Update document pending tagging names state
      setDocumentsPendingTaggingNames(call.documentsPendingTaggingNames || []);

      if (!isEqual(callDetails, call)) {
        setCallDetails(call)(dispatch);
      }
    } catch (e) {
      const { message } = parseException(e);
      // Stop polling and redirect to room page when unauthorized
      if (message.toLowerCase().includes('not authorized')) {
        videoProvider.leaveSession();
        history.push(`/notary/rooms/${roomId}`);
        return;
      }
      throw e;
    }
  };

  useInterval(fetchCallDetails, CALL_DETAILS_REFRESH_INTERVAL);

  return { documentsPendingTaggingNames };
};
